import React from "react";
import {
  Box,
  Column,
  Columns,
  Heading,
  StackSection,
} from "@origin-digital/ods-core";
import { useFeatureFlag } from "@origin-digital/feature-flags";
import { navTo } from "@origin-digital/event-dispatcher";
import {
  interaction,
  type IInteractionData,
} from "@origin-digital/reporting-client";

import { TileButton } from "./TileButton";
import {
  content,
  PICTOGRAM_HELP_SUPPORT,
  PICTOGRAM_QUICK_SIGN_UP,
} from "./content";
import { useAuthStatus } from "./useAuthStatus";
import { config } from "../../config";

const trackOpenChatWidget = (isNativePlatform: boolean): void => {
  interaction("click", {
    componentType: "button",
    id: "open-chat",
    parentContainer: "intercom",
    label: "open chat widget",
    isNative: isNativePlatform,
    eVar10: "Yes",
  } as IInteractionData);
};

const SectionHeading: React.FC = () => (
  <Box marginBottom={{ xs: "medium", lg: "xxlarge" }}>
    <Heading component="h2" variant="h3" weight="regular" align="center">
      {content.heading}
    </Heading>
  </Box>
);

type NeedHelpProps = {
  loginOnly?: boolean;
  contactUsOnly?: boolean;
};

export const NeedHelp: React.FC<NeedHelpProps> = ({
  loginOnly = false,
  contactUsOnly = false,
}) => {
  const isChatForContactUsEnabled = useFeatureFlag("chatForContactUs");
  const { isAuth, isNativePlatform, isResolved } = useAuthStatus();
  const baseUrl = config.baseUrl;

  const handleContactUs = (event: React.MouseEvent<HTMLElement>): void => {
    event.preventDefault();
    if (isChatForContactUsEnabled && isAuth) {
      trackOpenChatWidget(isNativePlatform);
      navTo.messageUs(undefined);
      return;
    }
    navTo.contactUs(undefined);
  };

  const showLogin = isResolved && !isAuth;
  const showContactUs = !loginOnly;
  const showHeading = showLogin || showContactUs;

  return (
    <StackSection hideGutter={{ xs: true, sm: false }} width="xlarge">
      <Box id="need-help" marginX={{ xs: "medium", sm: "none" }}>
        {showHeading && <SectionHeading />}
        <Columns
          space={{ xs: "medium", lg: "large" }}
          collapseBelow="lg"
          alignX="center"
          alignY="stretch"
        >
          {isResolved && !isAuth && !contactUsOnly ? (
            <Column {...(loginOnly ? { width: "1/2" } : {})}>
              <TileButton
                dataId="login-tile"
                title={content.loginTitle}
                imageUrl={PICTOGRAM_QUICK_SIGN_UP}
                href={`${baseUrl}/auth/callback`}
              />
            </Column>
          ) : null}
          {showContactUs ? (
            <Column width="1/2">
              <TileButton
                dataId="contact-us"
                handleTileClick={handleContactUs}
                title={content.getInContactTitle}
                imageUrl={PICTOGRAM_HELP_SUPPORT}
              />
            </Column>
          ) : null}
        </Columns>
      </Box>
    </StackSection>
  );
};
